import { DataSource } from "@seriously-rad/seriouslyrad-framework-core/Data/DataSource";
import { PartialNullable } from "@seriously-rad/seriouslyrad-framework-core/Extensions";
import { IJNativeEvent } from "@seriously-rad/seriouslyrad-framework-core/JNative";
import { PropertyChange } from "@seriously-rad/seriouslyrad-framework-core/PropertyChange";
import { BlockEditor } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/BlockEditor";
import { ControlRef } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ControlRef";
import { InputControlPropertyValues } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/InputControl";
import { Toolbar } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Toolbar";
import { ToolbarButton, ToolbarButtonModeEnum } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ToolbarButton";
import { ToolbarComboBox } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ToolbarComboBox";
import { ToolbarSplitter } from "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ToolbarSplitter";
import { JsxFragment } from "@seriously-rad/seriouslyrad-framework-core/Ui/JsxFragment";
import { Editor, Extensions } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";

import "./EmailBlockEditor.scss";

const
    backgroundColor = {
        default: null,
        renderHTML: (attributes: any) =>
        {
            if (!attributes.backgroundColor)
                return {};

            return {
                style: `background-color: ${attributes.backgroundColor}`,
            };
        },
        parseHTML: (element: HTMLElement) =>
        {
            return element.style.backgroundColor.replace(/['"]+/g, "");
        },
    },
    borderCollapse = {
        default: null,
        renderHTML: (attributes: any) =>
        {
            if (!attributes.borderCollapse)
                return {};

            return {
                style: `border-collapse: ${attributes.borderCollapse}`,
            };
        },
        parseHTML: (element: HTMLElement) =>
        {
            return element.style.borderCollapse.replace(/['"]+/g, "");
        },
    },
    height = {
        default: null,
        renderHTML: (attributes: any) =>
        {
            if (!attributes.height)
                return {};

            return {
                height: parseInt(attributes.height),
                style: `height: ${attributes.height}`,
            };
        },
        parseHTML: (element: HTMLElement) =>
        {
            const
                height = parseInt(element.attributes.getNamedItem("height")?.value ?? element.style.height.replace(/['"]+/g, ""));

            return !isNaN(height) ? `${height}px` : undefined;
        },
    },
    width = {
        default: null,
        renderHTML: (attributes: any) =>
        {
            if (!attributes.width)
                return {};

            return {
                style: `width: ${attributes.width}`,
                width: parseInt(attributes.width),
            };
        },
        parseHTML: (element: HTMLElement) =>
        {
            const
                width = parseInt(element.attributes.getNamedItem("width")?.value ?? element.style.width.replace(/['"]+/g, ""));

            return !isNaN(width) ? `${width}px` : undefined;
        },
    };

export class EmailBlockEditor extends BlockEditor
{
    protected _toolbarButtonHighlight?: ControlRef<ToolbarButton>;
    protected _toolbarComboBoxColorRef?: ControlRef<ToolbarComboBox>;
    protected _toolbarComboBoxFontFamilyRef?: ControlRef<ToolbarComboBox>;
    protected _toolbarComboBoxFontSizeRef?: ControlRef<ToolbarComboBox>;

    constructor(propertyValues?: PartialNullable<InputControlPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("gme-control-email-block-editor");
    }

    protected override __initializeProperties(propertyValues: InputControlPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._toolbarButtonHighlight = new ControlRef();
        this._toolbarComboBoxColorRef = new ControlRef();
        this._toolbarComboBoxFontFamilyRef = new ControlRef();
        this._toolbarComboBoxFontSizeRef = new ControlRef();
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this._toolbarButtonHighlight?.current?.on("stateChanged", { self: this }, this.onHighlightStateChanged);
        this._toolbarComboBoxColorRef?.current?.on("valueChanged", { self: this }, this.onToolbarComboBoxColorValueChanged);
        this._toolbarComboBoxFontFamilyRef?.current?.on("valueChanged", { self: this }, this.onToolbarComboBoxFontFamilyValueChanged);
        this._toolbarComboBoxFontSizeRef?.current?.on("valueChanged", { self: this }, this.onToolbarComboBoxFontSizeValueChanged);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[]): JsxFragment
    {
        const
            colors = [
                { value: "rgb(229, 175, 0)", description: "** Brand - Gold" },
                { value: "rgb(255, 98, 68)", description: "** Brand - Orange" },
                { value: "rgb(61, 0, 102)", description: "** Brand - Purple" },
                { value: "rgb(0, 164, 159)", description: "** Brand - Teal" },

                { value: "rgb(0, 0, 0)", description: "Black" },
                { value: "rgb(48, 48, 48)", description: "Grey - Darker" },
                { value: "rgb(51, 51, 51)", description: "Grey - Dark" },
                { value: "rgb(119, 120, 119)", description: "Grey - Light" },
                { value: "rgb(192, 0, 0)", description: "Red - Soft" },
                { value: "rgb(255, 0, 0)", description: "Red - Hard" },
                { value: "rgb(255, 255, 255)", description: "White" },
            ],
            fontSizesPts = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72],
            fontSizes = fontSizesPts.map(item => ({ value: `${item}pt`, description: `${item}` }));

        return (
            <div>
                <Toolbar>
                    <ToolbarButton ref={this._toolbarButtonBold} icon="bold" mode={ToolbarButtonModeEnum.toggle} text="Bold" />
                    <ToolbarButton ref={this._toolbarButtonItalic} icon="italic" mode={ToolbarButtonModeEnum.toggle} text="Italic" />
                    <ToolbarButton ref={this._toolbarButtonUnderline} icon="underline" dividing={true} mode={ToolbarButtonModeEnum.toggle} text="Underline" />
                    <ToolbarButton ref={this._toolbarButtonBlockquote} icon="indent" mode={ToolbarButtonModeEnum.toggle} text="Blockquote" />
                    <ToolbarButton ref={this._toolbarButtonListBullets} icon="list ul" mode={ToolbarButtonModeEnum.toggle} text="Bullets" />
                    <ToolbarButton ref={this._toolbarButtonListNumbers} icon="list ol" dividing={true} mode={ToolbarButtonModeEnum.toggle} text="Numbering" />
                    <ToolbarButton ref={this._toolbarButtonHighlight} icon="highlighter" dividing={true} mode={ToolbarButtonModeEnum.toggle} text="Highlight" />
                    <ToolbarButton ref={this._toolbarButtonLink} icon="linkify" text="Link" />
                    <ToolbarButton ref={this._toolbarButtonUnlink} dividing={true} enabled={false} icon="unlink" text="Unlink" />
                    <ToolbarButton ref={this._toolbarButtonCode} icon="code" mode={ToolbarButtonModeEnum.toggle} text="HTML" />
                    <ToolbarComboBox ref={this._toolbarComboBoxFontFamilyRef} allowEmptySelection={true} cssClass="gme-font-family" listDataSource={new DataSource({ data: [{ value: "Arial", description: "Arial" }] })} />
                    <ToolbarComboBox ref={this._toolbarComboBoxFontSizeRef} allowEmptySelection={true} cssClass="gme-font-size" listDataSource={new DataSource({ data: fontSizes })} />
                    <ToolbarComboBox ref={this._toolbarComboBoxColorRef} allowEmptySelection={true} cssClass="gme-color" listDataSource={new DataSource({ data: colors })} />
                    <ToolbarSplitter />
                </Toolbar>
                <div ref={this._editorContainerRef} class="editor-container"></div>
                <textarea ref={this._textAreaContainerRef}></textarea>
                {children}
            </div>
        );
    }
    protected override _getExtensions(): Extensions
    {
        return [
            ...super._getExtensions(),
            Highlight.configure({
            }),
            Image.configure({
                inline: true,
            }),
            Table.extend({
                addAttributes()
                {
                    return {
                        ...(this as any).parent?.(),
                        backgroundColor,
                        borderCollapse,
                    }
                },
            }),
            TableHeader.extend({
                addAttributes()
                {
                    return {
                        ...(this as any).parent?.(),
                        backgroundColor,
                    }
                },
            }),
            TableRow.extend({
                addAttributes()
                {
                    return {
                        ...(this as any).parent?.(),
                        backgroundColor,
                    }
                },
            }),
            TableCell.extend({
                addAttributes()
                {
                    return {
                        ...(this as any).parent?.(),
                        backgroundColor,
                        height,
                        width,
                    }
                },
            }),
            TextAlign.configure({
                types: ["heading", "paragraph", "tableCell"],
            }),
            TextStyleKit,
        ];
    }
    protected override _updateToolbarLayout()
    {
        super._updateToolbarLayout();

        const
            codeView = this._toolbarButtonCode?.current?.state.get();

        this._toolbarButtonHighlight?.current?.enabled.set(!codeView);
        this._toolbarComboBoxColorRef?.current?.enabled.set(!codeView);
        this._toolbarComboBoxFontFamilyRef?.current?.enabled.set(!codeView);
        this._toolbarComboBoxFontSizeRef?.current?.enabled.set(!codeView);
    }

    protected override onSelectionUpdate(editor: Editor)
    {
        super.onSelectionUpdate(editor);

        this._toolbarButtonHighlight?.current?.state.set(editor.isActive("highlight") ?? false);

        const
            fontFamilies = this._toolbarComboBoxFontFamilyRef?.current?.listDataSource.get()?.data.get()?.map((item: any) => item.value);

        let
            foundFontFamily = false;

        for (const fontFamily of fontFamilies)
        {
            if (editor.isActive("textStyle", { fontFamily }))
            {
                foundFontFamily = true;
                this._toolbarComboBoxFontFamilyRef?.current?.value.set(fontFamily);
                break;
            }
        }

        if (!foundFontFamily)
            this._toolbarComboBoxFontFamilyRef?.current?.value.set(null);

        const
            fontSizes = this._toolbarComboBoxFontSizeRef?.current?.listDataSource.get()?.data.get()?.map((item: any) => item.value);

        let
            foundFontSize = false;

        for (const fontSize of fontSizes)
        {
            if (editor.isActive("textStyle", { fontSize }))
            {
                foundFontSize = true;
                this._toolbarComboBoxFontSizeRef?.current?.value.set(fontSize);
                break;
            }
        }

        if (!foundFontSize)
            this._toolbarComboBoxFontSizeRef?.current?.value.set(null);

        const
            colors = this._toolbarComboBoxColorRef?.current?.listDataSource.get()?.data.get()?.map((item: any) => item.value);

        let
            foundColor = false;

        for (const color of colors)
        {
            if (editor.isActive("textStyle", { color }))
            {
                foundColor = true;
                this._toolbarComboBoxColorRef?.current?.value.set(color);
                break;
            }
        }

        if (!foundColor)
            this._toolbarComboBoxColorRef?.current?.value.set(null);
    }

    protected onHighlightStateChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setHighlight();
        else
            self._editor?.commands.unsetHighlight();
    }
    protected onToolbarComboBoxColorValueChanged(evt: IJNativeEvent, change: PropertyChange<string>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setColor(change.currentValue);
        else
            self._editor?.commands.unsetColor();
    }
    protected onToolbarComboBoxFontFamilyValueChanged(evt: IJNativeEvent, change: PropertyChange<string>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setFontFamily(change.currentValue);
        else
            self._editor?.commands.unsetFontFamily();
    }
    protected onToolbarComboBoxFontSizeValueChanged(evt: IJNativeEvent, change: PropertyChange<string>)
    {
        const
            self = evt.data.self as this;

        if (self.__settingToolbarButtonState)
            return;

        if (change.currentValue)
            self._editor?.commands.setFontSize(change.currentValue);
        else
            self._editor?.commands.unsetFontSize();
    }
}